import { useCallback, useEffect, useRef, useState } from 'react';

import qs from 'qs';
import { useHistory, useLocation } from 'react-router';

import useQuery from '~/hooks/useQuery';

import { parseSearchString } from '~/utils/url';

import { parsePromoTesterVoucherIds } from '../PromoTester/parseVoucherIds';

export interface PromoTesterUrlState {
  orderId?: string;
  codeName?: string;
  /** Comma-separated voucher UUIDs (form / discount calculator) — `voucherId` is a single-id alias in the query string */
  voucherIds?: string;
  /** From query `brand=` — deep links can target a tenant; compare to current admin tenant */
  brand?: string;
  applyWithOrignalUserAccount?: boolean;
  ignoreLuxPlusPricing?: boolean;
  logAfter?: Date;
  logBefore?: Date;
}

export interface UsePromoTesterUrlProps {
  brand: string;
}

export interface UsePromoTesterUrlReturn {
  urlState: PromoTesterUrlState;
  updateUrlState: (newState: Partial<PromoTesterUrlState>) => void;
}

function readVoucherIdsFromQuery(query: URLSearchParams): string | undefined {
  return query.get('voucherIds') || query.get('voucherId') || undefined;
}

function voucherQueryFieldsFromFormRaw(raw: string | undefined): { voucherId?: string; voucherIds?: string } {
  if (!raw?.trim()) {
    return {};
  }
  const ids = parsePromoTesterVoucherIds(raw);
  if (!ids?.length) {
    return {};
  }
  if (ids.length === 1) {
    return { voucherId: ids[0] };
  }
  return { voucherIds: raw.trim() };
}

function parsePromoTesterStateFromQuery(query: URLSearchParams): PromoTesterUrlState {
  return {
    orderId: query.get('orderId') || undefined,
    codeName: query.get('codeName') || undefined,
    voucherIds: readVoucherIdsFromQuery(query) || undefined,
    brand: query.get('brand')?.trim() || undefined,
    applyWithOrignalUserAccount: query.get('applyWithOrignalUserAccount') === 'true',
    ignoreLuxPlusPricing: !query.get('ignoreLuxPlusPricing') || query.get('ignoreLuxPlusPricing') === 'true',
    logAfter: query.get('logAfter') ? new Date(query.get('logAfter') || '') : undefined,
    logBefore: query.get('logBefore') ? new Date(query.get('logBefore') || '') : undefined,
  };
}

function buildPromoTesterSearch(location: { search: string }, state: PromoTesterUrlState) {
  const q: Record<string, unknown> = {
    ...parseSearchString(location.search),
  } as Record<string, unknown>;
  delete q.voucherId;
  delete q.voucherIds;

  if (state.orderId) {
    q.orderId = state.orderId;
  } else {
    delete q.orderId;
  }
  if (state.codeName) {
    q.codeName = state.codeName;
  } else {
    delete q.codeName;
  }

  Object.assign(q, voucherQueryFieldsFromFormRaw(state.voucherIds));

  if (state.applyWithOrignalUserAccount) {
    q.applyWithOrignalUserAccount = 'true';
  } else {
    delete q.applyWithOrignalUserAccount;
  }
  if (state.ignoreLuxPlusPricing === false) {
    q.ignoreLuxPlusPricing = 'false';
  } else {
    delete q.ignoreLuxPlusPricing;
  }
  if (state.logAfter) {
    q.logAfter = state.logAfter.toISOString();
  } else {
    delete q.logAfter;
  }
  if (state.logBefore) {
    q.logBefore = state.logBefore.toISOString();
  } else {
    delete q.logBefore;
  }

  return `?${qs.stringify(q)}`;
}

export default function usePromoTesterUrl({ brand: _brand }: UsePromoTesterUrlProps): UsePromoTesterUrlReturn {
  const history = useHistory();
  const location = useLocation();
  const locationRef = useRef(location);
  const query = useQuery();
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Parse URL parameters; React state updates only from the real URL (avoids re-syncing the form on every debounced edit)
  const [urlState, setUrlState] = useState<PromoTesterUrlState>(() => parsePromoTesterStateFromQuery(query));
  const pendingStateRef = useRef<PromoTesterUrlState>(urlState);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    pendingStateRef.current = urlState;
  }, [urlState]);

  // Debounced URL update function
  const debouncedUpdateUrl = useCallback(
    (updatedState: PromoTesterUrlState) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        const loc = locationRef.current;
        const newSearch = buildPromoTesterSearch(loc, updatedState);
        if (newSearch === loc.search) {
          return;
        }
        history.push({ ...loc, search: newSearch });
      }, 300);
    },
    [history],
  );

  // Merge into the latest pending state and debounce a push. Does not set local React state (that comes from the URL)
  const updateUrlState = useCallback(
    (newState: Partial<PromoTesterUrlState>) => {
      const updated = { ...pendingStateRef.current, ...newState };
      pendingStateRef.current = updated;
      debouncedUpdateUrl(updated);
    },
    [debouncedUpdateUrl],
  );

  // Sync URL state when query parameters change (back/forward, direct links, other UI)
  useEffect(() => {
    const newUrlState = parsePromoTesterStateFromQuery(query);
    setUrlState((prev) => (JSON.stringify(newUrlState) === JSON.stringify(prev) ? prev : newUrlState));
  }, [query]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    urlState,
    updateUrlState,
  };
}
